export type SortableItem = {
  id: string;
  sortOrder: number;
};

function resolveSortableLabel(item: SortableItem & { name?: string; title?: string }): string {
  return item.name ?? item.title ?? '';
}

export function sortBySortOrder<T extends SortableItem & { name?: string; title?: string }>(
  items: T[],
): T[] {
  return [...items].sort((left, right) => {
    const orderDiff = left.sortOrder - right.sortOrder;
    if (orderDiff !== 0) {
      return orderDiff;
    }

    return resolveSortableLabel(left).localeCompare(resolveSortableLabel(right), 'pt-BR');
  });
}

export function nextSortOrder<T extends { sortOrder: number }>(items: T[]): number {
  if (items.length === 0) {
    return 0;
  }

  return Math.max(...items.map((item) => item.sortOrder)) + 1;
}

export function findSiblingIndex<T>(
  items: T[],
  index: number,
  direction: 'up' | 'down',
  isSibling: (left: T, right: T) => boolean,
): number {
  const current = items[index];
  if (!current) {
    return -1;
  }

  if (direction === 'up') {
    for (let candidate = index - 1; candidate >= 0; candidate -= 1) {
      if (isSibling(items[candidate], current)) {
        return candidate;
      }
    }

    return -1;
  }

  for (let candidate = index + 1; candidate < items.length; candidate += 1) {
    if (isSibling(items[candidate], current)) {
      return candidate;
    }
  }

  return -1;
}

export async function swapSortOrder<T extends SortableItem>(
  items: T[],
  index: number,
  direction: 'up' | 'down',
  updateItem: (item: T, sortOrder: number) => Promise<void>,
  isSibling?: (left: T, right: T) => boolean,
): Promise<void> {
  const sorted = sortBySortOrder(items);
  const targetIndex = isSibling
    ? findSiblingIndex(sorted, index, direction, isSibling)
    : direction === 'up'
      ? index - 1
      : index + 1;

  if (targetIndex < 0 || targetIndex >= sorted.length) {
    return;
  }

  const current = sorted[index];
  const neighbor = sorted[targetIndex];

  await Promise.all([
    updateItem(current, neighbor.sortOrder),
    updateItem(neighbor, current.sortOrder),
  ]);
}
